import * as React from 'react'
import { View, Dimensions, Text } from 'react-native'
import styled from '@theme/styled-components'

import { Viewer } from '@components/plan/ViewerComponent'

import useReducer from '@store/useReducer'
import useI18n from '@i18n/useI18n'
import * as PlanStore from '@store/plan/plan'

import { ViperPin, ViperViewProps } from 'viper-viewer/dist/types'
import { FormikHelpers, useFormik } from 'formik'
import theme from '@theme/Theme'

type Values = {
  plan: PositionPlanIncident
}

const DEFAULT_INIT: ViperViewProps['initialPosition'] = {
  center: [2.273982551847581, 48.82340600971497],
  zoom: 14,
}

const Map = ({ form, onForm }: IncidentFormProps) => {
  const i18n = useI18n()
  const plans = useReducer(PlanStore.store, (s) => s.plans)

  const currentPlan = React.useMemo(() => {
    const current = plans
      .filter((plan) => !!plan.viewers.geojson && !!plan.viewers.geojson.path)
      .find((plan) => plan.localisationId === form.space)
    if (!!current && !!current.viewers.geojson) {
      return current.viewers.geojson
    }
    return undefined
  }, [form.space])

  const init: Values = React.useMemo(() => ({ plan: form.plan }), [form.plan])

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty || !currentPlan) {
      //submit on change
      handleSubmit()
    }
  }, [values, currentPlan])

  const onClickMap = (x: number, y: number) => {
    setFieldValue('plan', { x: x.toString(), y: y.toString() })
  }

  const pins: ViperPin[] = React.useMemo(
    () =>
      !!values.plan
        ? [
            {
              id: 'id',
              x: parseFloat(values.plan.x),
              y: parseFloat(values.plan.y),
              color: theme.colors.primary,
            },
          ]
        : [],
    [values]
  )

  const renderNoPlan = () => (
    <NoPlanContainer>
      <NoPlanText>{i18n.t('screens.map.noPlan')}</NoPlanText>
    </NoPlanContainer>
  )

  const renderViewer = (path: string, params?: PlanViewerParams) => {
    const initialPosition: ViperViewProps['initialPosition'] | undefined = params
      ? { center: params.center ? [params.center.x, params.center.y] : undefined, zoom: params.zoom || undefined }
      : undefined

    return (
      <Viewer
        key={path}
        planUrl={path}
        pins={pins}
        // TODO : recevoir ces positions par défaut, sinon le point de départ est arbitraire
        initialPosition={initialPosition || DEFAULT_INIT}
        onClickMap={onClickMap}
      />
    )
  }

  return (
    <View style={{ height: Dimensions.get('window').height }}>
      {!!currentPlan && !!currentPlan.path ? renderViewer(currentPlan.path, currentPlan.params) : renderNoPlan()}
    </View>
  )
}

export default Map

const NoPlanContainer = styled(View)`
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
const NoPlanText = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`
